// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { boxStickyStyles } from "@store/Themes/common/mixins/box";
import { baseGridHiddenProps } from "@store/Themes/common/props/baseGrid";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { buttonResetProps } from "@store/Themes/common/props/button";
import { focusUpdateStyles } from "@toolkit/Themes/common/mixins";
import loadCmsLibraryData from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/LoadCmsLibrary";
import { useIsLibraryFilterNavOpen } from "@store/Framework/Store/Store/Data/CmsLibrary/CmsLibraryHooks";
import setLibraryFilterNavIsOpen from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/SetLibraryFilterNavIsOpen";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";

const enum fields {
    librarySource = "librarySource",
}

const wrapperStyles = () => {
    return css`
        ${breakpointValues(pxToRemArray([20, null, 25, null, 30]), "margin-top")}

        &:first-child {
            margin-top: 0;
        }
    `;
};

const mapDispatchToProps = {
    loadCmsLibraryData,
    setLibraryFilterNavIsOpen,
};

const mapStateToProps = (state: ApplicationState) => {
    const { isLoading, parameters } = state.store.data.cmsLibrary;

    return {
        isLoading,
        parameters,
    };
};

export interface CmsLibraryTilesProps extends WidgetProps {
    fields: {
        [fields.librarySource]: string;
    };
}

type Props = CmsLibraryTilesProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CmsLibraryColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    drawer?: DrawerPresentationProps;
    drawerContentWrapper?: InjectableCss;
    drawerApplyWrapper?: InjectableCss;
    drawerApplyButton?: ButtonPresentationProps;
}

export const columnsStyles: CmsLibraryColumnsStyles = {
    container: {
        ...baseGridHiddenProps.container,
        css: css`
            ${baseGridHiddenProps.container?.css}
            ${wrapperStyles()}
        `,
    },
    leftColumnGridItem: baseGridHiddenProps.sideColumn,
    rightColumnGridItem: baseGridHiddenProps.mainColumn,
    drawer: {
        closeButtonProps: {
            ...buttonResetProps,
            css: css`
                ${buttonResetProps.css};
                ${focusUpdateStyles(true)};
                display: flex;
                height: 100%;
                padding: ${x => x.theme.design.menu.panel.rowPadding};
                padding-top: 0;
                padding-bottom: 0;
                color: ${getColor("primary")};
                transition: ${transitionHoverStyles("color")};

                &:hover,
                &:active {
                    color: ${getColor("primary.dark")};
                }
            `,
        },
        cssOverrides: {
            drawerContent: css`
                ${x => x.theme.drawer.defaultProps?.cssOverrides?.drawerContent}
                display: flex;
                height: 100%;
                flex-direction: column;
                justify-content: space-between;
            `,
        },
    },
    drawerContentWrapper: {
        css: css`
            padding: ${pxToRem(16)};
        `,
    },
    drawerApplyWrapper: {
        css: css`
            ${boxStickyStyles()}
            position: sticky;
            bottom: 0;
        `,
    },
    drawerApplyButton: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
            width: 100%;
        `,
    },
};

const styles = columnsStyles;

const CmsLibraryColumns: FC<Props> = ({
    id,
    fields: { librarySource },
    isLoading,
    parameters,
    loadCmsLibraryData,
    setLibraryFilterNavIsOpen,
}) => {
    const isFilterNavOpen = useIsLibraryFilterNavOpen();
    const closeDrawer = () => setLibraryFilterNavIsOpen({ isOpen: false });

    useEffect(() => {
        loadCmsLibraryData({
            libraryType: librarySource,
            isFirstLoad: true,
        });
    }, [librarySource]);

    useEffect(() => {
        if (!parameters || parameters.libraryType !== librarySource) {
            return;
        }

        loadCmsLibraryData(parameters);
    }, [parameters, librarySource]);

    return (
        <LoadingOverlay loading={isLoading}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.leftColumnGridItem}>
                    <Zone contentId={id} zoneName="Content00" />
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <Zone contentId={id} zoneName="Content01" />
                </GridItem>
            </GridContainer>
            <Drawer
                draggable
                position="left"
                isOpen={isFilterNavOpen}
                handleClose={closeDrawer}
                contentLabel={translate("Sort & Filter")}
                headline={translate("Sort & Filter")}
                {...styles.drawer}
            >
                <StyledWrapper {...styles.drawerContentWrapper}>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper {...styles.drawerApplyWrapper}>
                    <Button onClick={closeDrawer} {...styles.drawerApplyButton}>
                        {translate("Apply Filters")}
                    </Button>
                </StyledWrapper>
            </Drawer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CmsLibraryColumns),
    definition: {
        group: "Common",
        displayName: "CMS Library Columns",
        fieldDefinitions: [
            {
                name: fields.librarySource,
                displayName: "CMS Library Source",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Valves", value: "valves" },
                    { displayName: "Pumps", value: "pumps" },
                    { displayName: "Cyclones", value: "cyclones" },
                ],
                defaultValue: "valves",
                tooltip: `Select type of Library you want to display`,
                tooltipPosition: "left",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
